<template>
  <div class="hms-page">
    <Navigation />

    <div class="container-fluid hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-shield-check-outline me-2"></i>{{ $t('page.insurance', 'Insurance Claims') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-primary" @click="openCreate"><i class="mdi mdi-plus me-1"></i>New Claim</button>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- Stats -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="filters.status='draft'; loadAll()" :class="{ selected: filters.status==='draft' }">
          <div class="hms-stat-num">{{ stats.draft || 0 }}</div>
          <div class="hms-stat-lbl">Draft</div>
        </div>
        <div class="hms-stat" @click="filters.status='submitted'; loadAll()" :class="{ selected: filters.status==='submitted' }">
          <div class="hms-stat-num hms-stat-info">{{ stats.submitted || 0 }}</div>
          <div class="hms-stat-lbl">Submitted</div>
        </div>
        <div class="hms-stat" @click="filters.status='under_review'; loadAll()" :class="{ selected: filters.status==='under_review' }">
          <div class="hms-stat-num hms-stat-warning">{{ stats.under_review || 0 }}</div>
          <div class="hms-stat-lbl">Under Review</div>
        </div>
        <div class="hms-stat" @click="filters.status='approved'; loadAll()" :class="{ selected: filters.status==='approved' }">
          <div class="hms-stat-num hms-stat-success">{{ stats.approved || 0 }}</div>
          <div class="hms-stat-lbl">Approved</div>
        </div>
        <div class="hms-stat" @click="filters.status='rejected'; loadAll()" :class="{ selected: filters.status==='rejected' }">
          <div class="hms-stat-num hms-stat-danger">{{ stats.rejected || 0 }}</div>
          <div class="hms-stat-lbl">Rejected</div>
        </div>
        <div class="hms-stat" @click="filters.status='paid'; loadAll()" :class="{ selected: filters.status==='paid' }">
          <div class="hms-stat-num hms-stat-success">{{ stats.paid_30d || 0 }}</div>
          <div class="hms-stat-lbl">Paid (30d)</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-warning">KSh {{ formatNum(stats.pending_amount) }}</div>
          <div class="hms-stat-lbl">Pending Amount</div>
        </div>
        <div class="hms-stat">
          <div class="hms-stat-num hms-stat-success">KSh {{ formatNum(stats.reimbursed_30d) }}</div>
          <div class="hms-stat-lbl">Reimbursed (30d)</div>
        </div>
      </div>

      <!-- By provider -->
      <div class="hms-card mb-3" v-if="byProvider.length">
        <div class="hms-card-title"><i class="mdi mdi-chart-donut me-1"></i>By Insurer</div>
        <div class="hms-prov-grid">
          <div v-for="p in byProvider" :key="p.insurance_provider" class="hms-prov" @click="filters.provider = p.insurance_provider; loadAll()" :class="{ active: filters.provider === p.insurance_provider }">
            <div class="hms-prov-name">{{ p.insurance_provider }}</div>
            <div class="hms-prov-num">{{ p.cnt }}</div>
            <div class="hms-muted-sm">claims · KSh {{ formatNum(p.total_approved) }} approved</div>
          </div>
        </div>
      </div>

      <!-- Filter bar + table -->
      <div class="hms-card">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <div class="hms-search-wrap">
            <i class="mdi mdi-magnify"></i>
            <input class="hms-search" v-model="filters.search" placeholder="Search claim #, patient, SHA #..." @input="debouncedLoad" />
          </div>
          <select class="hms-input hms-filter" v-model="filters.status" @change="loadAll">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="partially_approved">Partially Approved</option>
            <option value="rejected">Rejected</option>
            <option value="paid">Paid</option>
          </select>
          <select class="hms-input hms-filter" v-model="filters.provider" @change="loadAll">
            <option value="">All insurers</option>
            <option v-for="p in providerNames" :key="p" :value="p">{{ p }}</option>
          </select>
          <button class="hms-btn hms-btn-amber" v-if="hasFilters" @click="resetFilters">
            <i class="mdi mdi-filter-off-outline me-1"></i>Clear
          </button>
          <span class="hms-muted ms-auto">{{ claims.length }} claims</span>
        </div>

        <div v-if="loading && !claims.length" class="hms-spinner"><div class="spinner-border"></div></div>
        <div v-else-if="claims.length" style="overflow-x:auto;">
          <table class="hms-table">
            <thead><tr>
              <th>Claim #</th>
              <th>Patient</th>
              <th>Insurer</th>
              <th>Member #</th>
              <th>Claim KSh</th>
              <th>Approved KSh</th>
              <th>Status</th>
              <th>Age</th>
              <th></th>
            </tr></thead>
            <tbody>
              <tr v-for="c in claims" :key="c.claim_id" @click="openDetail(c.claim_id)" class="hms-row-click">
                <td>
                  <div class="hms-mono fw-semibold">{{ c.claim_number }}</div>
                  <div class="hms-muted-sm">Inv {{ c.invoice_number }}</div>
                </td>
                <td>
                  <div class="fw-semibold">{{ c.patient_name }}</div>
                  <div class="hms-muted-sm hms-mono">{{ c.patient_no }}</div>
                </td>
                <td>{{ c.insurance_provider }}</td>
                <td><span class="hms-mono">{{ c.insurance_number || '—' }}</span></td>
                <td><strong>{{ formatNum(c.claim_amount) }}</strong></td>
                <td>
                  <strong v-if="c.approved_amount > 0" :class="approvedColor(c)">{{ formatNum(c.approved_amount) }}</strong>
                  <span v-else class="hms-muted-sm">—</span>
                </td>
                <td><span class="hms-badge" :class="statusBadge(c.status)">{{ formatStatus(c.status) }}</span></td>
                <td>
                  <div v-if="c.submitted_date" class="hms-muted-sm">{{ c.days_since_submission }}d</div>
                  <div v-else class="hms-muted-sm">—</div>
                </td>
                <td><i class="mdi mdi-chevron-right text-muted"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="hms-empty"><i class="mdi mdi-shield-search-outline"></i>No claims match.</div>
      </div>

      <!-- DETAIL MODAL -->
      <div class="hms-modal-overlay" v-if="detail" @click.self="detail=null">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span>
              <i class="mdi mdi-shield-check-outline me-1"></i>{{ detail.Claim.claim_number }}
              <span class="hms-badge ms-2" :class="statusBadge(detail.Claim.status)">{{ formatStatus(detail.Claim.status) }}</span>
            </span>
            <button class="hms-modal-close" @click="detail=null">&times;</button>
          </div>

          <div class="hms-kv mb-3">
            <div><span class="hms-kv-key">Patient</span><span><strong>{{ detail.Claim.patient_name }}</strong> <span class="hms-muted-sm">· {{ detail.Claim.patient_no }} · {{ ageStr(detail.Claim.dob) }} · {{ detail.Claim.gender }}</span></span></div>
            <div><span class="hms-kv-key">Insurer</span><span><strong>{{ detail.Claim.insurance_provider }}</strong></span></div>
            <div><span class="hms-kv-key">Member #</span><span class="hms-mono">{{ detail.Claim.insurance_number || '—' }}</span></div>
            <div><span class="hms-kv-key">Invoice</span><span class="hms-mono">{{ detail.Claim.invoice_number }}</span> <span class="hms-muted-sm">· {{ formatDate(detail.Claim.invoice_date) }}</span></div>
            <div><span class="hms-kv-key">Invoice Total</span><span>KSh {{ formatNum(detail.Claim.invoice_total) }} · Paid {{ formatNum(detail.Claim.invoice_paid) }} · Bal {{ formatNum(detail.Claim.invoice_balance) }}</span></div>
            <div><span class="hms-kv-key">Claim Amount</span><span><strong>KSh {{ formatNum(detail.Claim.claim_amount) }}</strong></span></div>
            <div v-if="detail.Claim.approved_amount > 0"><span class="hms-kv-key">Approved</span><span class="text-success"><strong>KSh {{ formatNum(detail.Claim.approved_amount) }}</strong></span></div>
            <div v-if="detail.Claim.rejected_amount > 0"><span class="hms-kv-key">Rejected</span><span class="text-danger">KSh {{ formatNum(detail.Claim.rejected_amount) }}</span></div>
            <div v-if="detail.Claim.submitted_date"><span class="hms-kv-key">Submitted</span><span>{{ formatDate(detail.Claim.submitted_date) }}</span></div>
            <div v-if="detail.Claim.approved_date"><span class="hms-kv-key">Decision Date</span><span>{{ formatDate(detail.Claim.approved_date) }}</span></div>
            <div v-if="detail.Claim.paid_date"><span class="hms-kv-key">Paid Date</span><span>{{ formatDate(detail.Claim.paid_date) }}</span></div>
            <div v-if="detail.Claim.rejection_reason"><span class="hms-kv-key">Reason / Notes</span><span class="hms-pre-text">{{ detail.Claim.rejection_reason }}</span></div>
          </div>

          <div class="hms-section-label">INVOICE LINE ITEMS</div>
          <table class="hms-table mb-3" v-if="detail.Items && detail.Items.length">
            <thead><tr><th>Description</th><th>Category</th><th>Qty</th><th>Unit</th><th>Total</th></tr></thead>
            <tbody>
              <tr v-for="(it, i) in detail.Items" :key="i">
                <td>{{ it.item_description }}</td>
                <td><span class="hms-badge hms-badge-info">{{ it.item_category }}</span></td>
                <td>{{ it.quantity }}</td>
                <td>{{ formatNum(it.unit_price) }}</td>
                <td><strong>{{ formatNum(it.total_price) }}</strong></td>
              </tr>
            </tbody>
          </table>

          <!-- Workflow buttons -->
          <div class="d-flex gap-2 flex-wrap mt-3">
            <!-- Draft → submit -->
            <template v-if="detail.Claim.status === 'draft'">
              <button class="hms-btn hms-btn-blue" @click="previewSha" v-if="isSha(detail.Claim)">
                <i class="mdi mdi-eye-outline me-1"></i>Preview SHA Payload
              </button>
              <button class="hms-btn hms-btn-primary" @click="submitClaim">
                <i class="mdi mdi-send me-1"></i>{{ isSha(detail.Claim) ? 'Submit to SHA' : 'Mark Submitted' }}
              </button>
              <button class="hms-btn hms-btn-danger ms-auto" @click="deleteClaim"><i class="mdi mdi-trash-can-outline me-1"></i>Delete Draft</button>
            </template>

            <!-- Submitted / under review → record decision -->
            <template v-else-if="detail.Claim.status === 'submitted' || detail.Claim.status === 'under_review'">
              <button class="hms-btn hms-btn-success" @click="openDecide('approved')">
                <i class="mdi mdi-check-circle-outline me-1"></i>Approve
              </button>
              <button class="hms-btn hms-btn-warning" @click="openDecide('partially_approved')">
                <i class="mdi mdi-circle-half-full me-1"></i>Partially Approve
              </button>
              <button class="hms-btn hms-btn-danger" @click="openDecide('rejected')">
                <i class="mdi mdi-close-circle-outline me-1"></i>Reject
              </button>
            </template>

            <!-- Approved → mark paid -->
            <template v-else-if="detail.Claim.status === 'approved' || detail.Claim.status === 'partially_approved'">
              <button class="hms-btn hms-btn-success" @click="openPay">
                <i class="mdi mdi-cash-check me-1"></i>Mark Paid
              </button>
              <button class="hms-btn hms-btn-warning" @click="openDecide('rejected')">
                <i class="mdi mdi-close-circle-outline me-1"></i>Mark Rejected
              </button>
            </template>

            <!-- Rejected — allow re-create or delete -->
            <template v-else-if="detail.Claim.status === 'rejected'">
              <button class="hms-btn hms-btn-danger ms-auto" @click="deleteClaim"><i class="mdi mdi-trash-can-outline me-1"></i>Archive</button>
            </template>
          </div>
        </div>
      </div>

      <!-- CREATE MODAL -->
      <div class="hms-modal-overlay" v-if="showCreate" @click.self="showCreate=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-plus me-1"></i>New Insurance Claim</span>
            <button class="hms-modal-close" @click="showCreate=false">&times;</button>
          </div>
          <div v-if="createError" class="hms-alert hms-alert-danger">{{ createError }}</div>
          <div class="hms-info-banner mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            Pick an invoice flagged as <strong>insurance_pending</strong>. Provider + member number are pre-filled from patient record where available.
          </div>

          <label class="hms-label">Invoice *</label>
          <select class="hms-input mb-3" v-model="newClaim.invoice_id" @change="onInvoicePicked">
            <option value="">— Select invoice —</option>
            <option v-for="i in claimableInvoices" :key="i.invoice_id" :value="i.invoice_id">
              {{ i.invoice_number }} · {{ i.first_name }} {{ i.last_name }} · KSh {{ formatNum(i.balance) }} ({{ i.insurance_provider || 'no provider set' }})
            </option>
          </select>
          <div v-if="!claimableInvoices.length" class="hms-muted-sm mb-3">No insurance-pending invoices. Mark an invoice's payment_status as insurance_pending in Billing first.</div>

          <div class="row g-3 mb-3" v-if="newClaim.invoice_id">
            <div class="col-md-6"><label class="hms-label">Insurer *</label>
              <select class="hms-input" v-model="newClaim.insurance_provider">
                <option value="SHA">SHA (Social Health Authority)</option>
                <option value="Britam">Britam</option>
                <option value="Jubilee">Jubilee</option>
                <option value="AAR">AAR</option>
                <option value="CIC">CIC</option>
                <option value="UAP">UAP</option>
                <option value="Madison">Madison</option>
                <option value="Corporate">Corporate scheme</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Member / SHA Number</label>
              <input class="hms-input" v-model="newClaim.insurance_number" placeholder="e.g. SHA-PT-4456" />
            </div>
            <div class="col-12"><label class="hms-label">Claim Amount (KSh) *</label>
              <input type="number" class="hms-input" v-model.number="newClaim.claim_amount" step="1" min="0" />
              <div class="hms-muted-sm mt-1">Defaults to the invoice total. Adjust if claiming partial coverage.</div>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="submitCreate" :disabled="processing || !newClaim.invoice_id">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            Create Draft Claim
          </button>
        </div>
      </div>

      <!-- DECIDE MODAL -->
      <div class="hms-modal-overlay" v-if="decideMode" @click.self="decideMode=null">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span>
              <i class="mdi" :class="decideIcon"></i> Record decision: {{ decideLabel }}
            </span>
            <button class="hms-modal-close" @click="decideMode=null">&times;</button>
          </div>
          <div class="hms-info-banner mb-3"><i class="mdi mdi-information-outline me-1"></i>Claim total: <strong>KSh {{ formatNum(detail && detail.Claim.claim_amount) }}</strong></div>

          <div v-if="decideMode === 'partially_approved'">
            <label class="hms-label">Approved Amount *</label>
            <input type="number" class="hms-input mb-3" v-model.number="decideAmount" step="1" min="0" :max="detail && detail.Claim.claim_amount" />
          </div>
          <div v-else-if="decideMode === 'approved'">
            <label class="hms-label">Approved Amount</label>
            <input type="number" class="hms-input mb-3" v-model.number="decideAmount" step="1" />
            <div class="hms-muted-sm mb-2">Leave as full claim amount if fully approved.</div>
          </div>

          <div v-if="decideMode === 'rejected' || decideMode === 'partially_approved'">
            <label class="hms-label">{{ decideMode === 'rejected' ? 'Rejection Reason *' : 'Reason for partial approval' }}</label>
            <textarea class="hms-input mb-3" rows="3" v-model="decideReason" placeholder="e.g. Pre-authorisation missing for procedure X; Item Y outside formulary"></textarea>
          </div>

          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="decideMode=null">Cancel</button>
            <button class="hms-btn hms-btn-primary flex-fill" @click="confirmDecide" :disabled="processing">Confirm</button>
          </div>
        </div>
      </div>

      <!-- PAY MODAL -->
      <div class="hms-modal-overlay" v-if="showPay" @click.self="showPay=false">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-cash-check me-1"></i>Record Reimbursement</span>
            <button class="hms-modal-close" @click="showPay=false">&times;</button>
          </div>
          <div class="hms-info-banner mb-3" v-if="detail">
            <i class="mdi mdi-information-outline me-1"></i>
            Approved: <strong>KSh {{ formatNum(detail.Claim.approved_amount) }}</strong>. Paying it creates a payment row against invoice {{ detail.Claim.invoice_number }} and updates its balance.
          </div>
          <label class="hms-label">Paid Amount *</label>
          <input type="number" class="hms-input mb-3" v-model.number="payAmount" step="1" />
          <label class="hms-label">Payment Reference</label>
          <input class="hms-input mb-3" v-model="payReference" placeholder="EFT / cheque / SHA reference" />
          <div class="d-flex gap-2">
            <button class="hms-btn hms-btn-amber flex-fill" @click="showPay=false">Cancel</button>
            <button class="hms-btn hms-btn-success flex-fill" @click="confirmPay" :disabled="processing || payAmount <= 0">Confirm Reimbursement</button>
          </div>
        </div>
      </div>

      <!-- SHA PAYLOAD PREVIEW MODAL -->
      <div class="hms-modal-overlay" v-if="shaPayload" @click.self="shaPayload=null">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-shield-key-outline me-1"></i>SHA Submission Payload</span>
            <button class="hms-modal-close" @click="shaPayload=null">&times;</button>
          </div>
          <div class="hms-info-banner mb-3">
            <i class="mdi mdi-information-outline me-1"></i>
            This is the JSON payload that will be submitted to SHA. Configure <span class="hms-mono">sha_api_url</span> and <span class="hms-mono">sha_api_key</span> in <a href="javascript:void(0)" @click="$router.push('/settings'); shaPayload=null">Settings → Advanced</a> to enable direct API submission. Without those keys, claims are marked submitted and you upload the payload manually via the SHA portal.
          </div>
          <pre class="hms-pre-json">{{ shaPayloadStr }}</pre>
          <div class="d-flex gap-2 mt-3">
            <button class="hms-btn hms-btn-blue flex-fill" @click="copyPayload"><i class="mdi mdi-content-copy me-1"></i>{{ copied ? 'Copied' : 'Copy JSON' }}</button>
            <button class="hms-btn hms-btn-blue flex-fill" @click="downloadPayload"><i class="mdi mdi-download me-1"></i>Download JSON</button>
            <button class="hms-btn hms-btn-amber flex-fill" @click="shaPayload=null">Close</button>
          </div>
        </div>
      </div>

      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding: 20px 24px 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.text-success { color: var(--hms-success); }
.text-danger { color: var(--hms-danger); }
.text-muted { color: var(--hms-m); }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 18px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 140px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { transform: translateY(-2px); border-color: var(--hms-primary); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 18px; font-weight: 800; color: var(--hms-h); line-height: 1.2; }
.hms-stat-lbl { font-size: 11px; color: var(--hms-s); margin-top: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
.hms-stat-info    { color: var(--hms-info); }
.hms-stat-warning { color: var(--hms-warning); }
.hms-stat-success { color: var(--hms-success); }
.hms-stat-danger  { color: var(--hms-danger); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; }
.hms-card-title { color: var(--hms-h); font-weight: 700; font-size: 13px; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 1px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-prov-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 8px; }
.hms-prov { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 10px 14px; cursor: pointer; }
.hms-prov:hover, .hms-prov.active { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-prov-name { color: var(--hms-h); font-weight: 600; font-size: 13px; }
.hms-prov-num { color: var(--hms-primary); font-weight: 800; font-size: 22px; line-height: 1.1; }

.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 340px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-filter { width: auto; padding: 7px 12px; font-size: 13px; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }

.hms-table { width: 100%; border-collapse: collapse; }
.hms-table th { color: var(--hms-s); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); text-align: left; white-space: nowrap; }
.hms-table td { color: var(--hms-h); font-size: 13px; padding: 10px 12px; border-bottom: 1px solid var(--hms-br); vertical-align: top; }
.hms-row-click { cursor: pointer; }
.hms-row-click:hover td { background: var(--hms-primary-lt); }

.hms-badge { padding: 2px 10px; border-radius: 50px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-info    { background: var(--hms-primary-lt); color: var(--hms-primary); }
.hms-badge-warning { background: rgba(255,193,7,.15); color: #ca8a04; }
.hms-badge-danger  { background: rgba(220,53,69,.15); color: var(--hms-danger); }
.hms-badge-success { background: rgba(40,167,69,.15); color: var(--hms-success); }
.hms-badge-purple  { background: rgba(155,89,182,.15); color: #9b59b6; }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-success { background: var(--hms-success); color: #fff; }
.hms-btn-success:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-warning { background: rgba(255,193,7,.15); color: #ca8a04; border: 1px solid rgba(255,193,7,.3); }
.hms-btn-warning:hover:not(:disabled) { background: rgba(255,193,7,.25); }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-danger:hover:not(:disabled) { background: var(--hms-danger); color: #fff; }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 760px; max-height: calc(100vh - 80px); overflow-y: auto; }
.hms-modal-sm { max-width: 480px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-section-label { font-size: 11px; font-weight: 700; color: var(--hms-s); letter-spacing: 1.5px; margin-bottom: 8px; }
.hms-kv { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 12px 16px; }
.hms-kv > div { display: flex; padding: 6px 0; border-bottom: 1px dashed var(--hms-br); font-size: 13px; color: var(--hms-h); gap: 8px; }
.hms-kv > div:last-child { border-bottom: none; }
.hms-kv-key { color: var(--hms-s); flex: 0 0 140px; font-weight: 600; }
.hms-pre-text { white-space: pre-wrap; }
.hms-pre-json { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 8px; padding: 12px 14px; color: var(--hms-h); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; max-height: 400px; overflow: auto; white-space: pre-wrap; }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-info-banner a { color: var(--hms-primary); text-decoration: underline; font-weight: 600; }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "InsuranceClaims",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false,
      claims: [], stats: {}, byProvider: [],
      filters: { status: '', provider: '', search: '' },
      _searchTimer: null,

      detail: null, showCreate: false, createError: '',
      claimableInvoices: [],
      newClaim: { invoice_id: '', insurance_provider: 'SHA', insurance_number: '', claim_amount: 0 },

      decideMode: null, decideAmount: 0, decideReason: '',
      showPay: false, payAmount: 0, payReference: '',

      shaPayload: null, copied: false,

      toastMsg: '', toastError: false, toastTimer: null
    };
  },
  computed: {
    providerNames() { return this.byProvider.map(p => p.insurance_provider); },
    hasFilters() { var f = this.filters; return !!(f.status || f.provider || f.search); },
    decideLabel() { return ({ approved:'Approved', partially_approved:'Partially Approved', rejected:'Rejected', under_review:'Under Review' })[this.decideMode] || ''; },
    decideIcon() { return ({ approved:'mdi-check-circle-outline', partially_approved:'mdi-circle-half-full', rejected:'mdi-close-circle-outline', under_review:'mdi-clock-outline' })[this.decideMode] || ''; },
    shaPayloadStr() { return this.shaPayload ? JSON.stringify(this.shaPayload, null, 2) : ''; }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    var t = localStorage.getItem("hms_theme") || "light";
    document.documentElement.setAttribute("data-theme", t);
    this.loadAll();
  },
  beforeUnmount() {
    if (this._searchTimer) clearTimeout(this._searchTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => self.toastMsg = '', 2400);
    },
    debouncedLoad() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      var self = this;
      this._searchTimer = setTimeout(() => self.loadAll(), 350);
    },
    async loadAll() {
      this.loading = true;
      try {
        var r = await executeJson(Object.assign({ function: "getInsuranceClaims" }, this.filters), hms);
        if (r.data && r.data.success) {
          this.claims     = (r.data.data && r.data.data.Claims)     || [];
          this.stats      = (r.data.data && r.data.data.Stats)      || {};
          this.byProvider = (r.data.data && r.data.data.ByProvider) || [];
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    resetFilters() { this.filters = { status: '', provider: '', search: '' }; this.loadAll(); },

    async openDetail(claimId) {
      try {
        var r = await executeJson({ function: "getInsuranceClaimDetail", claim_id: claimId }, hms);
        if (r.data && r.data.success) this.detail = r.data.data;
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    },

    async openCreate() {
      this.showCreate = true; this.createError = '';
      this.newClaim = { invoice_id: '', insurance_provider: 'SHA', insurance_number: '', claim_amount: 0 };
      try {
        var r = await executeJson({ function: "getClaimableInvoices" }, hms);
        if (r.data && r.data.success) this.claimableInvoices = (r.data.data && r.data.data.Invoices) || [];
      } catch(e) {}
    },
    onInvoicePicked() {
      var inv = this.claimableInvoices.find(i => i.invoice_id == this.newClaim.invoice_id);
      if (inv) {
        if (inv.insurance_provider) this.newClaim.insurance_provider = inv.insurance_provider;
        if (inv.insurance_number)   this.newClaim.insurance_number   = inv.insurance_number;
        this.newClaim.claim_amount = parseFloat(inv.balance) || 0;
      }
    },
    async submitCreate() {
      this.createError = '';
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "createInsuranceClaim" }, this.newClaim), hms);
        if (r.data && r.data.success) {
          this.showToast('Claim drafted'); this.showCreate = false; await this.loadAll();
          await this.openDetail(r.data.data.claim_id);
        } else this.createError = r.data.message || 'Failed';
      } catch(e) { this.createError = 'Connection error'; }
      this.processing = false;
    },

    async submitClaim() {
      this.processing = true;
      try {
        var fn = this.isSha(this.detail.Claim) ? "submitClaimToSha" : "submitInsuranceClaim";
        var r = await executeJson({ function: fn, claim_id: this.detail.Claim.claim_id }, hms);
        if (r.data && r.data.success) {
          if (r.data.data && r.data.data.payload) {
            this.shaPayload = r.data.data.payload;
            this.showToast(r.data.data.message || 'Submitted (manual mode)');
          } else {
            this.showToast(r.data.message || 'Submitted');
          }
          await this.refreshDetail();
          await this.loadAll();
        } else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },

    async previewSha() {
      try {
        var r = await executeJson({ function: "getShaClaimPayload", claim_id: this.detail.Claim.claim_id }, hms);
        if (r.data && r.data.success) this.shaPayload = (r.data.data && r.data.data.Payload) || null;
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    },

    openDecide(mode) {
      this.decideMode = mode;
      this.decideAmount = mode === 'rejected' ? 0 : (this.detail ? parseFloat(this.detail.Claim.claim_amount) || 0 : 0);
      this.decideReason = '';
    },
    async confirmDecide() {
      if (this.decideMode === 'rejected' && !this.decideReason) { this.showToast('Reason required for rejection', true); return; }
      if (this.decideMode === 'partially_approved' && (this.decideAmount <= 0 || this.decideAmount >= this.detail.Claim.claim_amount)) {
        this.showToast('Approved amount must be > 0 and < claim total for partial approval', true); return;
      }
      this.processing = true;
      try {
        var r = await executeJson({
          function: "decideInsuranceClaim",
          claim_id: this.detail.Claim.claim_id,
          decision: this.decideMode,
          approved_amount: this.decideAmount,
          rejection_reason: this.decideReason
        }, hms);
        if (r.data && r.data.success) {
          this.showToast('Decision recorded'); this.decideMode = null;
          await this.refreshDetail(); await this.loadAll();
        } else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },

    openPay() {
      this.payAmount = parseFloat(this.detail.Claim.approved_amount) || 0;
      this.payReference = '';
      this.showPay = true;
    },
    async confirmPay() {
      this.processing = true;
      try {
        var r = await executeJson({
          function: "markInsuranceClaimPaid",
          claim_id: this.detail.Claim.claim_id,
          paid_amount: this.payAmount,
          payment_reference: this.payReference
        }, hms);
        if (r.data && r.data.success) {
          this.showToast('Reimbursement recorded · invoice updated'); this.showPay = false;
          await this.refreshDetail(); await this.loadAll();
        } else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
      this.processing = false;
    },

    async deleteClaim() {
      if (!confirm('Delete this claim?')) return;
      try {
        var r = await executeJson({ function: "deleteInsuranceClaim", claim_id: this.detail.Claim.claim_id }, hms);
        if (r.data && r.data.success) { this.showToast('Deleted'); this.detail = null; await this.loadAll(); }
        else this.showToast(r.data.message || 'Failed', true);
      } catch(e) { this.showToast('Connection error', true); }
    },

    async refreshDetail() {
      if (!this.detail) return;
      await this.openDetail(this.detail.Claim.claim_id);
    },

    copyPayload() {
      if (!this.shaPayload) return;
      var self = this;
      navigator.clipboard.writeText(this.shaPayloadStr).then(() => {
        self.copied = true;
        setTimeout(() => self.copied = false, 1500);
      });
    },
    downloadPayload() {
      if (!this.shaPayload) return;
      var blob = new Blob([this.shaPayloadStr], { type: 'application/json' });
      var link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'sha-claim-' + (this.detail ? this.detail.Claim.claim_number : 'payload') + '.json';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    },

    isSha(c) { return c && (c.insurance_provider || '').toUpperCase().startsWith('SHA'); },
    statusBadge(s) { return ({ draft:'hms-badge-neutral', submitted:'hms-badge-info', under_review:'hms-badge-warning', approved:'hms-badge-success', partially_approved:'hms-badge-warning', rejected:'hms-badge-danger', paid:'hms-badge-success' })[s] || 'hms-badge-neutral'; },
    formatStatus(s) { return ({ draft:'Draft', submitted:'Submitted', under_review:'Under Review', approved:'Approved', partially_approved:'Partial', rejected:'Rejected', paid:'Paid' })[s] || s; },
    approvedColor(c) { return parseFloat(c.approved_amount) >= parseFloat(c.claim_amount) ? 'text-success' : 'text-warning'; },
    formatNum(n) { if (n == null || n === '') return '0'; return Number(n).toLocaleString(); },
    ageStr(dob) { if (!dob||dob==='0000-00-00'||dob==='1900-01-01') return '?'; var d=new Date(dob); if (isNaN(d)) return '—'; return Math.floor((Date.now()-d.getTime())/(365.25*24*3600000))+'y'; },
    formatDate(s) { if (!s) return '—'; var d = new Date(s.replace ? s.replace(' ','T') : s); return isNaN(d) ? s : d.toLocaleDateString(); }
  }
};
</script>
